import { Loader2Icon } from 'lucide-react'
// TODO(icons): approx — was Lucide CircleCheckIcon, OctagonXIcon, TriangleAlertIcon
import { CheckOutlineIcon, InfoIcon, ErrorIcon, WarningIcon } from '@/src/icons'
import { Toaster as Sonner, type ToasterProps } from 'sonner'

/*
 * Toast (Sonner) — DS alignment (Phase 3C)
 *
 * DS Toast variant colours (from Elements DS page 8403149):
 *   Success  — bg green-700  (#15822B), text white
 *   Error    — bg red-600    (#C91428), text white
 *   Warning  — bg yellow-400 (#FFAF29), text neutral-900
 *   Info     — bg blue-600   (#006BD6), text white
 *   Neutral  — bg neutral-700 (#415161), text white
 *
 * Toast backgrounds use dark fills (not the light fills used by Section Message).
 * These are solid, dark-background toasts — the DS deliberately contrasts them
 * against page content for visibility.
 *
 * Sonner CSS variable overrides:
 *   --normal-bg / --normal-border / --normal-text  → neutral toast
 *   --success-bg / --success-text / --success-border
 *   --error-bg   / --error-text   / --error-border
 *   --warning-bg / --warning-text / --warning-border
 *   --info-bg    / --info-text    / --info-border
 *
 * Icon size: DS icon-md = 20px (size-5), per plan/04-principal-decisions.md §Dimensions 2.
 *
 * Note: Sonner's style prop only accepts inline CSS values — CSS custom properties
 * referencing OKLCH vars are not reliably resolved by Sonner's injection mechanism,
 * so we read the DS-specified hex values directly here. These values are immutable
 * DS primitives (not semantic aliases), so hardcoding is intentional.
 *
 * DS Page: 8403149
 */

const Toaster = ({ theme = 'light', ...props }: ToasterProps) => {
  return (
    <Sonner
      theme={theme}
      className="toaster group"
      icons={{
        /* DS icon-md = 20px */
        success: <CheckOutlineIcon />,
        info: <InfoIcon />,
        warning: <WarningIcon />,
        error: <ErrorIcon />,
        loading: <Loader2Icon className="size-5 animate-spin" />,
      }}
      toastOptions={{
        unstyled: false,
        classNames: {
          /* min-w-56 = size-56 ≈ 224px; max-w-112 = size-112 ≈ 448px per DS */
          toast: 'rounded-lg text-sm min-w-56 max-w-[28rem]',
        },
      }}
      style={
        {
          /* Neutral — DS neutral-700 (#415161) bg, white text */
          '--normal-bg': '#415161',
          '--normal-text': '#ffffff',
          '--normal-border': '#415161',
          /* Success — DS green-700 (#15822B) bg, white text */
          '--success-bg': '#15822b',
          '--success-text': '#ffffff',
          '--success-border': '#15822b',
          /* Error — DS red-600 (#C91428) bg, white text */
          '--error-bg': '#c91428',
          '--error-text': '#ffffff',
          '--error-border': '#c91428',
          /* Warning — DS yellow-400 (#FFAF29) bg, neutral-900 dark text */
          '--warning-bg': '#ffaf29',
          '--warning-text': '#222b33',
          '--warning-border': '#ffaf29',
          /* Info — DS blue-600 (#006BD6) bg, white text */
          '--info-bg': '#006bd6',
          '--info-text': '#ffffff',
          '--info-border': '#006bd6',
          /* Radius — DS border-radius-l = 8px */
          '--border-radius': '8px',
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

export { Toaster }
export type { ToasterProps }
